'use client';

import Link, { useLinkStatus } from 'next/link';
import { ChevronLeft, ChevronRight, type LucideIcon } from 'lucide-react';
import { type FC, type ReactNode } from 'react';

import { Button, SEAM } from '@/components/ui/button/button';
import type { TNumberedPages } from './numbered';
import { cn } from '@/utils/cn';

/**
 * One pagination for every list page (spec 013, 017): the blog and the
 * auction listings by number, the provider-path listings by opaque cursor.
 *
 * Previous and next are drawn as ONE control cut by a diagonal seam — the
 * same slant the cards cut their photos with — with "Następne" overlapping.
 * No counter anywhere: cursor paging has no total, and a number on two of
 * three pages would make the third look broken.
 *
 * A click is answered at once: `useLinkStatus` swaps the chevron for a spinner
 * while the next page streams, on top of the site-wide progress bar.
 */

/**
 * Hrefs arrive as strings, never as a builder: this is a client component and
 * a function cannot cross the server boundary — the blog page found that out
 * with an error boundary.
 */
export type TPagination = {
  prevHref: string | null;
  nextHref: string | null;
  /** When present, page numbers are drawn between the two arrows. */
  pages?: TNumberedPages;
  /**
   * Adds `nofollow` to both links. The auction listing sets it (spec 016,
   * FR-017): every cursor page is a distinct provider query and the views are
   * `noindex` anyway, so a crawler walking them spends allowance for nothing.
   * The blog does not — its pages are indexable and meant to be walked.
   */
  nofollow?: boolean;
};

/** Rendered INSIDE the Link, where `useLinkStatus` knows about its navigation. */
const PendingAware: FC<{ icon: LucideIcon; label: string; iconFirst: boolean }> = ({
  icon: Icon,
  label,
  iconFirst,
}) => {
  const { pending } = useLinkStatus();
  const icon = pending ? (
    <span
      aria-hidden
      className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
    />
  ) : (
    <Icon size={18} aria-hidden />
  );

  return (
    <span className={cn('inline-flex items-center gap-1.5 uppercase tracking-wider', pending && 'opacity-80')}>
      {iconFirst && icon}
      <span className="hidden sm:inline">{label}</span>
      {!iconFirst && icon}
    </span>
  );
};

const PageControl: FC<{
  href: string | null;
  rel: 'prev' | 'next';
  label: string;
  nofollow: boolean;
}> = ({ href, rel, label, nofollow }) => {
  const Icon = rel === 'prev' ? ChevronLeft : ChevronRight;
  const iconFirst = rel === 'prev';
  const shape = iconFirst ? SEAM.label.prev : SEAM.label.next;

  if (!href) {
    return (
      <Button as="span" variant="ghost" aria-disabled className={cn(shape, SEAM.off)}>
        <span className="inline-flex items-center gap-1.5 uppercase tracking-wider">
          {iconFirst && <Icon size={18} aria-hidden />}
          <span className="hidden sm:inline">{label}</span>
          {!iconFirst && <Icon size={18} aria-hidden />}
        </span>
      </Button>
    );
  }

  return (
    <Button
      as={Link}
      href={href}
      rel={nofollow ? `nofollow ${rel}` : rel}
      aria-label={label}
      className={shape}
    >
      <PendingAware icon={Icon} label={label} iconFirst={iconFirst} />
    </Button>
  );
};

/**
 * The numbers (spec 017): the mode control's own vocabulary — every piece a
 * compact segment cut on both sides by the arrows' diagonal, joined along
 * 2 px seams — the current page the primary (gold) face, the others the
 * outlined secondary face, the gap and the phone counter outlined but inert, so the line runs the whole bar. One
 * bar, one language, nothing invented for this control (owner feedback,
 * 2026-09-10, after a strip-with-cells version that never sat right).
 */
const SEGMENT =
  '[clip-path:polygon(18px_0,100%_0,calc(100%-18px)_100%,0_100%)] -ml-4 rounded-none pl-6 pr-6 z-[5] min-w-0 font-oswald tabular-nums hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.35)]';

const PageNumber: FC<{ page: number; href: string; current: boolean; nofollow: boolean }> = ({ page, href, current, nofollow }) => {
  if (current) {
    return (
      <Button as="span" aria-current="page" className={cn(SEGMENT, 'z-[6]')}>
        {page}
      </Button>
    );
  }

  return (
    <Button as={Link} href={href} variant="secondary" rel={nofollow ? 'nofollow' : undefined} aria-label={`Strona ${page}`} className={SEGMENT}>
      {page}
    </Button>
  );
};

const Gap: FC<{ children: ReactNode; className?: string }> = ({ children, className }) => (
  <Button
    as="span"
    variant="secondary"
    aria-hidden
    className={cn(SEGMENT, 'pointer-events-none pl-5 pr-5 text-action/50 hover:border-action/40 hover:bg-transparent hover:shadow-none', className)}
  >
    {children}
  </Button>
);

export const Pagination: FC<TPagination> = ({ prevHref, nextHref, pages, nofollow = false }) => {
  if (!prevHref && !nextHref && !pages) return null;
  if (pages && pages.count <= 1) return null;

  const first = pages?.items[0]?.page ?? 1;
  const last = pages?.items.at(-1)?.page ?? pages?.count ?? 1;

  return (
    <nav aria-label="Nawigacja po stronach" className="inline-flex items-stretch">
      <PageControl href={prevHref} rel="prev" label="Poprzednie" nofollow={nofollow} />
      {pages && (
        <span className="hidden items-stretch sm:inline-flex">
          {first > 1 && pages.firstHref && <PageNumber page={1} href={pages.firstHref} current={false} nofollow={nofollow} />}
          {first > 2 && <Gap>…</Gap>}
          {pages.items.map(item => (
            <PageNumber key={item.page} page={item.page} href={item.href} current={item.page === pages.current} nofollow={nofollow} />
          ))}
          {last < pages.count - 1 && <Gap>…</Gap>}
          {last < pages.count && pages.lastHref && <PageNumber page={pages.count} href={pages.lastHref} current={false} nofollow={nofollow} />}
        </span>
      )}
      {pages && (
        <Gap className="text-foreground/80 sm:hidden">
          {pages.current} / {pages.count}
        </Gap>
      )}
      <PageControl href={nextHref} rel="next" label="Następne" nofollow={nofollow} />
    </nav>
  );
};
